import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import Modal from '../components/Modal';
import EnrollmentForm from '../components/EnrollmentForm';
import EmptyState from '../components/EmptyState';
import { checkEnrollmentRequirements } from '../utils/diagnosticHelper';
import { getApiErrorMessage } from '../utils/apiError';
import './Enrollments.css';

const isIsoDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value || '');

const Enrollments = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEnrollment, setEditingEnrollment] = useState(null);
  const [showForm, setShowForm] = useState(false); // Estado para mostrar formulario inline
  const [filterStudent, setFilterStudent] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [dateFromInput, setDateFromInput] = useState('');
  const [dateToInput, setDateToInput] = useState('');
  const [studentSearchText, setStudentSearchText] = useState('');
  const [debouncedStudentSearch, setDebouncedStudentSearch] = useState('');
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedStudentSearch(studentSearchText.trim()), 300);
    return () => clearTimeout(timer);
  }, [studentSearchText]);

  // Obtener estudiantes para el filtro
  const { data: students } = useQuery('students', async () => {
    const response = await api.get('/students');
    return response.data;
  });

  // Obtener grupos para el filtro
  const { data: groups } = useQuery('groups', async () => {
    const response = await api.get('/groups');
    return response.data;
  });

  // Obtener disciplinas, niveles y horarios para diagnóstico
  const { data: disciplines } = useQuery('disciplines', async () => {
    const response = await api.get('/disciplines');
    return response.data;
  });

  const { data: levels } = useQuery('levels', async () => {
    const response = await api.get('/levels');
    return response.data;
  });

  const enrollmentDiagnostic = React.useMemo(() => {
    return checkEnrollmentRequirements({
      students: students || [],
      disciplines: disciplines || [],
      groups: groups || [],
      levels: levels || []
    });
  }, [students, disciplines, groups, levels]);

  // Obtener matrículas
  const { data: enrollments, isLoading, error } = useQuery(
    ['enrollments', filterStudent, filterGroup, filterStatus, filterDateFrom, filterDateTo],
    async () => {
      const params = {};
      if (filterStudent) params.student_id = filterStudent;
      if (filterGroup) params.group_id = filterGroup;
      if (filterStatus) params.status = filterStatus;
      if (isIsoDate(filterDateFrom)) params.date_from = filterDateFrom;
      if (isIsoDate(filterDateTo)) params.date_to = filterDateTo;
      const response = await api.get('/enrollments', { params });
      return response.data;
    },
    {
      onError: (err) => {
        console.error('Error al cargar matrículas:', err);
        toast.error(getApiErrorMessage(err, 'Error al cargar matrículas'));
      }
    }
  );

  const createEnrollmentMutation = useMutation(
    (data) => {
      const {
        discipline_id,
        collect_payment_now,
        initial_payment,
        ...enrollmentFields
      } = data;
      if (collect_payment_now && initial_payment) {
        return api.post('/enrollments/with-initial-payment', {
          ...enrollmentFields,
          collect_payment_now: true,
          initial_payment,
        });
      }
      return api.post('/enrollments', enrollmentFields);
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('enrollments');
        queryClient.invalidateQueries('students');
        queryClient.invalidateQueries('groups');
        queryClient.invalidateQueries('payments');
        const hadPayment = response?.data?.payment?.paid_amount > 0;
        toast.success(
          hadPayment
            ? 'Matrícula creada y cobro registrado correctamente.'
            : 'Matrícula creada exitosamente!'
        );
        setIsModalOpen(false);
        setShowForm(false);
        setEditingEnrollment(null);
      },
      onError: (err) => {
        const errorDetail = err.response?.data?.detail;
        if (Array.isArray(errorDetail)) {
          // Mapear nombres de campos técnicos a nombres amigables
          const fieldNames = {
            'student_id': 'Estudiante',
            'discipline_id': 'Disciplina',
            'level_id': 'Nivel',
            'group_id': 'Grupo',
            'start_date': 'Fecha de inicio',
            'end_date': 'Fecha de fin',
            'monthly_fee': 'Cuota mensual',
            'discount_percentage': 'Porcentaje de descuento',
            'discount_amount': 'Monto de descuento',
            'period_type': 'Tipo de período'
          };
          
          errorDetail.forEach(e => {
            const fieldName = fieldNames[e.loc[1]] || e.loc[1];
            toast.error(`❌ ${fieldName}: ${e.msg}`, { autoClose: 6000 });
          });
        } else {
          toast.error(errorDetail || 'Error al crear matrícula');
        }
        // No cerrar el modal si hay error para que el usuario pueda corregir
      },
    }
  );

  const updateEnrollmentMutation = useMutation(
    ({ id, data }) => api.put(`/enrollments/${id}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('enrollments');
        toast.success('Matrícula actualizada exitosamente!');
        setIsModalOpen(false);
        setShowForm(false);
        setEditingEnrollment(null);
      },
      onError: (err) => {
        const errorDetail = err.response?.data?.detail;
        if (Array.isArray(errorDetail)) {
          // Mapear nombres de campos técnicos a nombres amigables
          const fieldNames = {
            'student_id': 'Estudiante',
            'discipline_id': 'Disciplina',
            'level_id': 'Nivel',
            'group_id': 'Grupo',
            'start_date': 'Fecha de inicio',
            'end_date': 'Fecha de fin',
            'monthly_fee': 'Cuota mensual',
            'discount_percentage': 'Porcentaje de descuento',
            'discount_amount': 'Monto de descuento',
            'period_type': 'Tipo de período'
          };
          
          errorDetail.forEach(e => {
            const fieldName = fieldNames[e.loc[1]] || e.loc[1];
            toast.error(`❌ ${fieldName}: ${e.msg}`, { autoClose: 6000 });
          });
        } else {
          toast.error(errorDetail || 'Error al actualizar matrícula');
        }
      },
    }
  );

  const handleCreateClick = () => {
    setEditingEnrollment(null);
    setShowForm(true);
  };

  // Abrir modal automáticamente si viene con action=create
  useEffect(() => {
    if (searchParams.get('action') === 'create') {
      handleCreateClick();
      // Limpiar el parámetro de la URL
      setSearchParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleEditClick = (enrollment) => {
    setEditingEnrollment(enrollment);
    setShowForm(true);
  };

  const selectedEnrollments = (enrollments || []).filter((enrollment) => selectedIds.includes(enrollment.id));

  const toggleEnrollmentSelection = (enrollmentId) => {
    setSelectedIds((prev) =>
      prev.includes(enrollmentId) ? prev.filter((id) => id !== enrollmentId) : [...prev, enrollmentId]
    );
  };

  const toggleAllVisible = () => {
    const visibleIds = (enrollments || []).map((enrollment) => enrollment.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
    setSelectedIds(allSelected ? [] : visibleIds);
  };

  const handleSubmit = (data) => {
    // Asegurar que las fechas se envíen como strings YYYY-MM-DD sin conversión
    // El input type="date" ya devuelve strings en formato YYYY-MM-DD
    // NO crear objetos Date porque causan problemas de zona horaria
    const processedData = { ...data };
    
    // Procesar monthly_fee: convertir a número válido
    if (data.monthly_fee !== '' && data.monthly_fee !== null && data.monthly_fee !== undefined) {
      const fee = parseFloat(data.monthly_fee);
      processedData.monthly_fee = !isNaN(fee) && fee >= 0 ? fee : 0;
    } else {
      processedData.monthly_fee = 0;
    }
    
    // Procesar discount_percentage: convertir a número válido o 0 si está vacío/null/undefined
    // El backend espera un Decimal, así que debe ser un número válido
    if (data.discount_percentage !== '' && 
        data.discount_percentage !== null && 
        data.discount_percentage !== undefined) {
      const discount = parseFloat(data.discount_percentage);
      processedData.discount_percentage = !isNaN(discount) && discount >= 0 ? discount : 0;
    } else {
      processedData.discount_percentage = 0;
    }
    
    // Las fechas del input type="date" ya vienen como strings YYYY-MM-DD
    // Solo asegurarnos de que no se conviertan a Date o ISO
    // Si por alguna razón vienen como Date, extraer la fecha local sin UTC
    if (processedData.start_date && processedData.start_date instanceof Date) {
      // Solo si es Date, extraer usando métodos locales (no UTC)
      const year = processedData.start_date.getFullYear();
      const month = String(processedData.start_date.getMonth() + 1).padStart(2, '0');
      const day = String(processedData.start_date.getDate()).padStart(2, '0');
      processedData.start_date = `${year}-${month}-${day}`;
    }
    // Si es string, mantenerlo tal cual (ya debería ser YYYY-MM-DD)
    
    if (processedData.end_date && processedData.end_date instanceof Date) {
      // Solo si es Date, extraer usando métodos locales (no UTC)
      const year = processedData.end_date.getFullYear();
      const month = String(processedData.end_date.getMonth() + 1).padStart(2, '0');
      const day = String(processedData.end_date.getDate()).padStart(2, '0');
      processedData.end_date = `${year}-${month}-${day}`;
    }
    // Si es string, mantenerlo tal cual (ya debería ser YYYY-MM-DD)
    
    if (editingEnrollment) {
      updateEnrollmentMutation.mutate({ id: editingEnrollment.id, data: processedData });
    } else {
      createEnrollmentMutation.mutate(processedData);
    }
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      'active': { label: 'Activa', class: 'badge-success' },
      'suspended': { label: 'Suspendida', class: 'badge-warning' },
      'cancelled': { label: 'Cancelada', class: 'badge-danger' },
      'completed': { label: 'Completada', class: 'badge-info' },
    };
    return statusMap[status] || { label: status, class: 'badge-secondary' };
  };

  const { data: studentSearchResults = [] } = useQuery(
    ['students-enrollment-filter', debouncedStudentSearch],
    async () => {
      const response = await api.get('/students', {
        params: { search: debouncedStudentSearch, limit: 20, is_active: true },
      });
      return response.data;
    },
    { enabled: showStudentDropdown && debouncedStudentSearch.length > 0 }
  );

  // Manejar selección de estudiante del dropdown
  const handleSelectStudent = (student) => {
    setFilterStudent(student.id);
    setStudentSearchText(`${student.first_name} ${student.last_name}`);
    setShowStudentDropdown(false);
  };

  // Limpiar filtro de estudiante
  const handleClearStudentFilter = () => {
    setFilterStudent('');
    setStudentSearchText('');
    setShowStudentDropdown(false);
  };

  // Manejar cambio en el input de búsqueda
  const handleStudentSearchChange = (e) => {
    const value = e.target.value;
    setStudentSearchText(value);
    setShowStudentDropdown(value.length > 0);
    if (value.length === 0) {
      setFilterStudent('');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    // Si es string YYYY-MM-DD, parsearlo directamente sin crear Date (evita problemas de zona horaria)
    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    }
    // Si es otro formato, intentar parsearlo
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    // Usar métodos locales para evitar conversión UTC
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${day}/${month}/${year}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(amount);
  };

  if (isLoading) {
    return <div className="loading">Cargando matrículas...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error">
          <h3>❌ Error al cargar matrículas</h3>
          <p>{getApiErrorMessage(error, 'Error desconocido')}</p>
          <button 
            className="btn btn-primary" 
            onClick={() => queryClient.invalidateQueries('enrollments')}
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h2>Gestión de Matrículas</h2>
        {!showForm && (
          <button className="btn btn-primary" onClick={handleCreateClick}>
            + Nueva Matrícula
          </button>
        )}
      </div>

      {showForm ? (
        <div className="card form-card">
          <div className="form-header">
            <h3>{editingEnrollment ? '✏️ Editar Matrícula' : '➕ Nueva Matrícula'}</h3>
            <button 
              className="btn btn-secondary"
              onClick={() => {
                setShowForm(false);
                setEditingEnrollment(null);
              }}
            >
              ← Volver a la lista
            </button>
          </div>
          <EnrollmentForm
            key={editingEnrollment ? `edit-${editingEnrollment.id}` : 'create'}
            initialData={editingEnrollment}
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowForm(false);
              setEditingEnrollment(null);
            }}
            isLoading={createEnrollmentMutation.isLoading || updateEnrollmentMutation.isLoading}
          />
        </div>
      ) : (
        <div className="card">
        <div className="filters-bar">
          <div className="autocomplete-wrapper">
            <div className="search-input-container">
              <input
                type="text"
                value={studentSearchText}
                onChange={handleStudentSearchChange}
                onFocus={() => studentSearchText && setShowStudentDropdown(true)}
                placeholder="Buscar estudiante por nombre o documento..."
                className="filter-input"
              />
              {studentSearchText && (
                <button
                  type="button"
                  onClick={handleClearStudentFilter}
                  className="clear-search-btn"
                  title="Limpiar búsqueda"
                >
                  ✕
                </button>
              )}
            </div>
            {showStudentDropdown && studentSearchResults.length > 0 && (
              <div className="autocomplete-dropdown">
                {studentSearchResults.map((student) => (
                  <div
                    key={student.id}
                    className={`autocomplete-item ${filterStudent === student.id ? 'selected' : ''}`}
                    onClick={() => handleSelectStudent(student)}
                  >
                    <div className="student-name">
                      {student.first_name} {student.last_name}
                    </div>
                    {student.document_number && (
                      <div className="student-doc">
                        {student.document_type}: {student.document_number}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {showStudentDropdown && debouncedStudentSearch && studentSearchResults.length === 0 && (
              <div className="autocomplete-dropdown">
                <div className="autocomplete-empty">
                  No se encontraron estudiantes
                </div>
              </div>
            )}
          </div>

          <select
            value={filterGroup}
            onChange={(e) => setFilterGroup(e.target.value)}
            className="filter-select"
          >
            <option value="">Todos los grupos</option>
            {groups?.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name} ({group.code})
              </option>
            ))}
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="">Todos los estados</option>
            <option value="active">Activa</option>
            <option value="suspended">Suspendida</option>
            <option value="cancelled">Cancelada</option>
            <option value="completed">Completada</option>
          </select>

          <form
            className="enrollment-date-filter"
            aria-label="Filtrar por fecha de matrícula"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              if (!e.currentTarget.checkValidity()) {
                toast.error('Completa una fecha válida en formato día, mes y año');
                return;
              }
              if (dateFromInput && dateToInput && dateFromInput > dateToInput) {
                toast.error('La fecha inicial no puede ser posterior a la fecha final');
                return;
              }
              setFilterDateFrom(dateFromInput);
              setFilterDateTo(dateToInput);
            }}
          >
            <label className="enrollment-date-field" htmlFor="enrollment-date-from">
              <span>Desde</span>
              <input
                id="enrollment-date-from"
                type="date"
                className="enrollment-date-input"
                value={dateFromInput}
                max={dateToInput || undefined}
                onChange={(e) => setDateFromInput(e.target.value)}
              />
            </label>
            <label className="enrollment-date-field" htmlFor="enrollment-date-to">
              <span>Hasta</span>
              <input
                id="enrollment-date-to"
                type="date"
                className="enrollment-date-input"
                value={dateToInput}
                min={dateFromInput || undefined}
                onChange={(e) => setDateToInput(e.target.value)}
              />
            </label>
            <button
              type="submit"
              className="enrollment-date-apply"
            >
              Filtrar fechas
            </button>
            {(dateFromInput || dateToInput || filterDateFrom || filterDateTo) && (
              <button
                type="button"
                className="enrollment-date-clear"
                onClick={() => {
                  setDateFromInput('');
                  setDateToInput('');
                  setFilterDateFrom('');
                  setFilterDateTo('');
                }}
              >
                Limpiar fechas
              </button>
            )}
          </form>
        </div>

        <div className="enrollments-bulk-bar">
          <button
            type="button"
            className="enrollments-bulk-btn"
            onClick={() => {
              if (selectedEnrollments.length !== 1) {
                toast.info('Selecciona una sola matrícula para editar');
                return;
              }
              handleEditClick(selectedEnrollments[0]);
            }}
            disabled={selectedEnrollments.length !== 1}
          >
            <span className="enrollments-bulk-icon" aria-hidden="true">✏️</span>
            Editar
          </button>
        </div>

        <div className="table-container enrollments-table-container">
          <table className="table enrollments-table">
            <thead>
              <tr>
                <th className="enrollments-col-check">
                  <input
                    type="checkbox"
                    aria-label="Seleccionar matrículas visibles"
                    checked={enrollments?.length > 0 && enrollments.every((enrollment) => selectedIds.includes(enrollment.id))}
                    onChange={toggleAllVisible}
                  />
                </th>
                <th>Estudiante</th>
                <th>Grupo</th>
                <th>Fecha Matrícula</th>
                <th>Fecha Inicio</th>
                <th>Fecha Fin</th>
                <th>Cuota Mensual</th>
                <th>Descuento</th>
                <th>Cuota Final</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {enrollments && enrollments.length > 0 ? (
                enrollments.map((enrollment) => {
                  const student = enrollment.student || students?.find(s => s.id === enrollment.student_id);
                  const group = groups?.find(g => g.id === enrollment.group_id);
                  const statusInfo = getStatusLabel(enrollment.status);
                  
                  return (
                    <tr key={enrollment.id} className={selectedIds.includes(enrollment.id) ? 'is-selected' : ''}>
                      <td className="enrollments-col-check">
                        <input
                          type="checkbox"
                          aria-label={`Seleccionar matrícula de ${student ? `${student.first_name} ${student.last_name}` : 'este alumno'}`}
                          checked={selectedIds.includes(enrollment.id)}
                          onChange={() => toggleEnrollmentSelection(enrollment.id)}
                        />
                      </td>
                      <td>
                        <strong>{student ? `${student.first_name} ${student.last_name}` : `ID: ${enrollment.student_id}`}</strong>
                      </td>
                      <td>
                        {group ? (
                          <span className="group-badge">
                            {group.discipline?.icon || '⚽'} {group.name}
                          </span>
                        ) : (
                          `ID: ${enrollment.group_id}`
                        )}
                      </td>
                      <td>{formatDate(enrollment.enrollment_date)}</td>
                      <td>{formatDate(enrollment.start_date)}</td>
                      <td>{formatDate(enrollment.end_date)}</td>
                      <td>{formatCurrency(enrollment.monthly_fee)}</td>
                      <td>
                        {enrollment.discount_percentage > 0 ? (
                          <span className="discount-badge">{enrollment.discount_percentage}%</span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>
                        <strong className="final-fee">{formatCurrency(enrollment.final_monthly_fee)}</strong>
                      </td>
                      <td>
                        <span className={`badge ${statusInfo.class}`}>
                          {statusInfo.label}
                        </span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="10" style={{ padding: 0, border: 'none' }}>
                    <EmptyState
                      icon="📝"
                      title="Aún no hay matrículas registradas"
                      message="Para crear tu primera matrícula, necesitas completar los siguientes requisitos:"
                      requirements={enrollmentDiagnostic.requirements}
                      primaryAction={enrollmentDiagnostic.allMet ? handleCreateClick : null}
                      primaryActionLabel="+ Crear Primera Matrícula"
                      hint={enrollmentDiagnostic.allMet 
                        ? "¡Todo listo! Puedes crear tu primera matrícula ahora."
                        : `Completa ${enrollmentDiagnostic.count.pending} requisito(s) más para poder crear matrículas.`}
                      className="compact"
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      <Modal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        title={editingEnrollment ? 'Editar Matrícula' : 'Nueva Matrícula'}
        closeOnOverlayClick={false}
      >
        <EnrollmentForm
          initialData={editingEnrollment}
          onSubmit={handleSubmit}
          onCancel={() => setIsModalOpen(false)}
          isLoading={createEnrollmentMutation.isLoading || updateEnrollmentMutation.isLoading}
        />
      </Modal>
    </div>
  );
};

export default Enrollments;
